import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SaveButton from '../components/SaveButton';
import ContactButton from '../components/ContactButton';
import { Button } from '../components/ui/button';
import { useAuth } from '../App';
import { toast } from 'sonner';
import {
  Home,
  MapPin,
  Bed,
  Bath,
  Maximize,
  Calendar,
  Mail,
  Phone,
  MessageCircle,
  Heart,
  Share2,
  ChevronLeft,
  Building,
  Check,
  Globe,
  GraduationCap,
  Shield,
  DollarSign,
  Loader2,
  Eye,
  Clock,
  Edit,
  Trash2
} from 'lucide-react';

const API = ''; // Use relative URL for same-origin API calls

const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProperty();
  }, [propertyId]);

  const fetchProperty = async () => {
    try {
      const res = await axios.get(`${API}/api/housing/${propertyId}`);
      setProperty(res.data);
    } catch (error) {
      toast.error('Property not found');
      navigate('/housing');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, currency, period, listingType) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0
    }).format(price);
    
    if (listingType === 'sale') return formatted;
    return `${formatted}/${period || 'month'}`;
  };

  const getListingTypeLabel = (type) => {
    switch (type) {
      case 'rent': return 'For Rent';
      case 'sale': return 'For Sale';
      case 'short-term': return 'Short-term Rental';
      default: return type;
    }
  };

  const getListingTypeColor = (type) => {
    switch (type) {
      case 'rent': return 'bg-blue-100 text-blue-700';
      case 'sale': return 'bg-green-100 text-green-700';
      case 'short-term': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleContact = (method) => {
    if (!user) {
      toast.error('Please sign in to contact the owner');
      return;
    }
    if (method === 'email' && property.contact_email) {
      window.location.href = `mailto:${property.contact_email}?subject=Inquiry about ${property.title}`;
    } else if (method === 'whatsapp' && property.contact_whatsapp) {
      window.open(`https://wa.me/${property.contact_whatsapp.replace(/\D/g, '')}?text=Hi, I'm interested in the property: ${property.title}`, '_blank');
    } else if (method === 'phone' && property.contact_phone) {
      window.location.href = `tel:${property.contact_phone}`;
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (!property) return null;

  const isOwner = user && (property.is_owner || property.owner_id === user.user_id || property.poster_user_id === user.user_id);
  const canEdit = property.can_edit || isOwner;

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this property listing? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/housing/${propertyId}`);
      toast.success('Property listing deleted');
      navigate('/housing');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete property listing');
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link 
              to="/housing" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to listings
            </Link>
            {isOwner && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(`/housing/${propertyId}/edit`)} data-testid="edit-property-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-property-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            )}
          </div>
        </div>


        {/* Main Content */}
        <div className="container mx-auto px-4 py-8">
          {/* Owner Actions Banner */}
          {canEdit && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 mb-6 flex items-center justify-between">
              <span className="text-sm font-medium text-primary">This is your listing</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/housing/${propertyId}/edit`)} data-testid="edit-property-banner-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="delete-property-banner-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Images & Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Gallery */}
              <div className="bg-white rounded-2xl border-2 border-border overflow-hidden">
                <div className="relative h-[400px] bg-gradient-to-br from-primary/20 to-accent/20">
                  {property.images?.length > 0 ? (
                    <img 
                      src={property.images[selectedImage]} 
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Building className="w-24 h-24 text-primary/40" />
                    </div>
                  )}
                  {/* Listing Type Badge */}
                  <div className="absolute top-4 left-4">
                    <span className={`px-4 py-2 rounded-full text-sm font-bold ${getListingTypeColor(property.listing_type)}`}>
                      {getListingTypeLabel(property.listing_type)}
                    </span>
                  </div>
                  {/* Action Buttons */}
                  <div className="absolute top-4 right-4 flex gap-2">
                    <SaveButton 
                      itemType="property" 
                      itemId={property.property_id}
                      variant="ghost"
                      className="p-3 bg-white/90 rounded-full hover:bg-white transition-colors"
                      showText={false}
                    />
                    <button className="p-3 bg-white/90 rounded-full hover:bg-white transition-colors">
                      <Share2 className="w-5 h-5 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                {/* Thumbnail Gallery */}
                {property.images?.length > 1 && (
                  <div className="p-4 flex gap-2 overflow-x-auto">
                    {property.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 ${
                          selectedImage === idx ? 'border-primary' : 'border-transparent'
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Property Info */}
              <div className="bg-white rounded-2xl border-2 border-border p-6">
                {/* Header */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                      {property.property_type}
                    </span>
                    {property.is_expat_friendly && (
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        Expat Friendly
                      </span>
                    )}
                  </div>
                  <h1 className="text-2xl md:text-3xl font-black mb-2">{property.title}</h1>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-5 h-5" />
                    <span>
                      {property.address && `${property.address}, `}
                      {property.district && `${property.district}, `}
                      {property.city}, {property.country}
                    </span>
                  </div>
                </div>

                {/* Key Features */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-xl mb-6">
                  <div className="text-center">
                    <Bed className="w-6 h-6 mx-auto text-primary mb-1" />
                    <div className="font-bold text-lg">{property.bedrooms}</div>
                    <div className="text-sm text-muted-foreground">Bedrooms</div>
                  </div>
                  <div className="text-center">
                    <Bath className="w-6 h-6 mx-auto text-primary mb-1" />
                    <div className="font-bold text-lg">{property.bathrooms}</div>
                    <div className="text-sm text-muted-foreground">Bathrooms</div>
                  </div>
                  <div className="text-center">
                    <Maximize className="w-6 h-6 mx-auto text-primary mb-1" />
                    <div className="font-bold text-lg">{property.size_sqm || '-'}</div>
                    <div className="text-sm text-muted-foreground">sqm</div>
                  </div>
                  <div className="text-center">
                    <Building className="w-6 h-6 mx-auto text-primary mb-1" />
                    <div className="font-bold text-lg">{property.floor || '-'}</div>
                    <div className="text-sm text-muted-foreground">Floor</div>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h2 className="text-xl font-bold mb-3">Description</h2>
                  <p className="text-muted-foreground whitespace-pre-line">{property.description}</p>
                </div>

                {/* Amenities */}
                {property.amenities?.length > 0 && (
                  <div className="mb-6">
                    <h2 className="text-xl font-bold mb-3">Amenities</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {property.amenities.map((amenity, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-sm">{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expat Features */}
                {(property.near_international_schools || property.near_expat_areas || property.visa_assistance) && (
                  <div>
                    <h2 className="text-xl font-bold mb-3">Expat-Friendly Features</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {property.near_international_schools && (
                        <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                          <GraduationCap className="w-6 h-6 text-blue-600" />
                          <span className="text-sm font-medium">Near International Schools</span>
                        </div>
                      )}
                      {property.near_expat_areas && (
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                          <Globe className="w-6 h-6 text-green-600" />
                          <span className="text-sm font-medium">In Expat-Friendly Area</span>
                        </div>
                      )}
                      {property.visa_assistance && (
                        <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl">
                          <Shield className="w-6 h-6 text-purple-600" />
                          <span className="text-sm font-medium">Visa Assistance Available</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Contact Card */}
            <div className="space-y-6">
              {/* Price Card */}
              <div className="bg-white rounded-2xl border-2 border-border p-6 sticky top-4">
                <div className="text-center mb-6">
                  <div className="text-sm text-muted-foreground mb-1">
                    {property.listing_type === 'sale' ? 'Asking Price' : 'Monthly Rent'}
                  </div>
                  <div className="text-4xl font-black text-primary">
                    {formatPrice(property.price, property.price_currency, property.price_period, property.listing_type)}
                  </div>
                  {property.deposit && property.listing_type !== 'sale' && (
                    <div className="text-sm text-muted-foreground mt-2">
                      Deposit: {new Intl.NumberFormat('en-US', { style: 'currency', currency: property.price_currency || 'USD', maximumFractionDigits: 0 }).format(property.deposit)}
                    </div>
                  )}
                </div>

                {property.available_from && (
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl mb-4">
                    <Calendar className="w-5 h-5 text-primary" />
                    <div>
                      <div className="text-sm text-muted-foreground">Available from</div>
                      <div className="font-medium">{property.available_from}</div>
                    </div>
                  </div>
                )}

                {/* Contact Buttons */}
                <div className="space-y-3">
                  {/* In-app messaging button */}
                  {property.owner_id && (
                    <ContactButton
                      recipientId={property.owner_id}
                      recipientName={property.contact_name || property.owner_name}
                      contextType="property"
                      contextId={property.property_id}
                      contextTitle={property.title}
                      className="w-full font-bold rounded-xl h-12 bg-teal-600 hover:bg-teal-700"
                    />
                  )}
                  {property.contact_email && (
                    <Button 
                      variant="outline"
                      className="w-full font-bold rounded-xl h-12"
                      onClick={() => handleContact('email')}
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      Email Owner
                    </Button>
                  )}
                  {property.contact_whatsapp && (
                    <Button 
                      variant="outline"
                      className="w-full font-bold rounded-xl h-12 border-green-500 text-green-600 hover:bg-green-50"
                      onClick={() => handleContact('whatsapp')}
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  )}
                  {property.contact_phone && (
                    <Button 
                      variant="outline"
                      className="w-full font-bold rounded-xl h-12"
                      onClick={() => handleContact('phone')}
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Call {property.contact_phone}
                    </Button>
                  )}
                </div>

                {/* Owner Info */}
                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Home className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-bold">{property.contact_name || property.owner_name}</div>
                      <div className="text-sm text-muted-foreground">Property Owner</div>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="mt-6 pt-6 border-t flex justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {property.views || 0} views
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(property.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PropertyDetailPage;
